import {useCallback, useState} from 'react';
import type {ArchivedQuestionModalType} from './ArchivedQuestionModal.react';

export interface UseArchivedQuestionModalOptions {
  questionSets?: any;
  serverArchivedQuestionCount?: number; // If the count is already known from the server
  type: ArchivedQuestionModalType;
}

export const checkFolderArchivedQuestionCount = (folder, savedQuestionSets) => {
  const archivedQuestionsFromMeta = folder?.meta?.get('count_of_archived_questions');
  if (archivedQuestionsFromMeta !== undefined) {
    return archivedQuestionsFromMeta;
  }
  return getArchivedQuestionCount(savedQuestionSets);
};

export const getArchivedQuestionCount = (questionSets: any): number => {
  if (!questionSets || !questionSets.reduce) {
    return 0;
  }

  return questionSets.reduce((totalCount, questionSet) => {
    const questions = questionSet.getQuestions?.();
    if (!questions) return totalCount;

    const archivedInThisSet =
      questions.filter((question) => {
        const status = question.get?.('status');
        return status === 'archived';
      }).size || 0;

    return totalCount + archivedInThisSet;
  }, 0);
};

export const useArchivedQuestionModal = ({
  questionSets,
  serverArchivedQuestionCount,
  type
}: UseArchivedQuestionModalOptions) => {
  const [showArchivedModal, setShowArchivedModal] = useState(false);
  const [archivedQuestionCount, setArchivedQuestionCount] = useState(0);
  const [modalResolve, setModalResolve] = useState<((value: boolean) => void) | null>(null);

  const countArchivedQuestions = useCallback((): number => {
    if (serverArchivedQuestionCount) {
      return serverArchivedQuestionCount;
    }
    if (questionSets) {
      return getArchivedQuestionCount(questionSets);
    }
    return 0;
  }, [questionSets, serverArchivedQuestionCount]);

  const showArchivedQuestionModal = useCallback((): Promise<boolean> => {
    const count = countArchivedQuestions();

    if (count === 0) {
      return Promise.resolve(true); // No archived questions, proceed
    }

    return new Promise((resolve) => {
      setArchivedQuestionCount(count);
      setShowArchivedModal(true);
      setModalResolve(() => resolve);
    });
  }, [countArchivedQuestions]);

  const handleArchivedModalConfirm = useCallback(() => {
    setShowArchivedModal(false);
    if (modalResolve) {
      modalResolve(true); // User confirmed
      setModalResolve(null);
    }
  }, [modalResolve]);

  const handleArchivedModalCancel = useCallback(() => {
    setShowArchivedModal(false);
    if (modalResolve) {
      modalResolve(false); // User cancelled
      setModalResolve(null);
    }
  }, [modalResolve]);

  const modalProps = {
    isOpen: showArchivedModal,
    archivedQuestionCount,
    type,
    onConfirm: handleArchivedModalConfirm,
    onCancel: handleArchivedModalCancel
  };

  return {
    showArchivedQuestionModal,
    modalProps,
    isArchivedModalOpen: showArchivedModal,
    countArchivedQuestions,
    getArchivedQuestionCount: countArchivedQuestions
  };
};
